import type {
  AddressDto,
  CallOutcomeDto,
  ClientDto,
  CoordsDto,
  EmailAddressDto,
  ExternalItemTypeDto,
  MemberProgressedDto,
  PhoneNumberDto,
  UserAppSettingsDto,
  VisitOutcomeDto,
} from '..';

import z from 'zod';
import type { Permission } from '../../../authorization';
import { Application, UserRole } from '../../../definitions';
import type { MemberSegmentDto } from './memberSegment.dto';
import type { MemberStatusChangeRequestDto } from './memberStatusChangeRequest.dto';
import type { MemberSummaryDto } from './memberSummary.dto';
import type { StandardMemberAttributesDto } from './standardMemberAttributes.dto';
import type { UserTeamDto } from './userTeam.dto';

export interface UserDataViewDto {
  users: UserDto[];
  memberStatusChangeRequests: MemberStatusChangeRequestDto[];
}

export interface UserDto {
  id: string;
  /** `iam.principals.id` when linked for IAM. */
  principalId?: string | null;
  interactableId: string;
  clientId: number;
  clientUserId: string;
  avonId?: string;
  name: string;
  firstName: string;
  lastName: string;
  addresses: AddressDto[];
  emailAddresses: EmailAddressDto[];
  phoneNumbers: PhoneNumberDto[];
  email: string;
  memberEmail: string;
  gender: string;
  preferredName: string;
  namePronunciation: string;
  pronouns: string;
  dateOfBirth: string;
  role: UserRole;
  avatar?: string;
  apps: Application[];
  assignedGuideId: string;
  assignedGuide?: string;
  expectedAssignedGuideId?: string;
  cadence: string;
  raceEthnicitySummary: string;
  reportedLanguage: string;
  monthlyOutreach: number;

  newlyEngagedMemberCount?: number;
  newlyEngagedTarget?: number;

  lastMessageSentAt: number;
  voicemailGreetingFileId: string;

  hasNew: boolean;
  hasAppAccess: boolean;
  lastEventAt: number;
  lastEventType: string;
  nextEventDate: string;
  nextEventTime: number;
  nextEventType: string;
  canBill: boolean;

  memberStatusChangeRequests: MemberStatusChangeRequestDto[];
  memberStatus: UserMemberStatusDto;
  memberStage: MemberStageDto;

  standardMemberAttributes: StandardMemberAttributesDto;
  memberSummary: MemberSummaryDto;

  memberSegments: MemberSegmentDto[];

  /** Hospitalization row ids for this member when loaded via include-details. */
  hospitalizationIds: number[];

  /**
   * True when the member's most recent qualifying hospitalization (HEDIS follow-up
   * type, ICD-10 present, within {@link HOSPITALIZATION_FOLLOW_UP_ELIGIBILITY_DAYS})
   * has not yet been linked to a follow-up interaction. Populated when member
   * details are loaded.
   */
  hasHospitalizationToFollowUp: boolean;

  /**
   * Admit/discharge date (`yyyy-MM-dd`) of the open qualifying hospitalization when
   * {@link hasHospitalizationToFollowUp} is true; otherwise null.
   */
  hospitalizationFollowUpEventDate: string | null;

  isDeleted: boolean;
  createdAt: number;
  updatedAt: number;
  dtoBonus: number;

  /**
   * Global (platform) permissions: from `UserRole` for most DTOs; for the acting
   * user on `/user/verify` this is the merge of role and IAM platform grants.
   */
  userPermissions: Permission[];
  memberPermissions: Permission[];
  appSettings?: UserAppSettingsDto;
  memberProgressed?: MemberProgressedDto;

  /**
   * Guide team memberships with optional nested team/clients when relations are loaded
   * (e.g. verify). Used for org-scoped UI such as Behavioral Health appointments.
   */
  teams?: UserTeamDto[];
}

export interface UserForGeoMapDto {
  id: string;
  firstName: string;
  lastName: string;
  memberStatus: UserMemberStatusDto;
  coords: CoordsDto;
}

export interface PaginatedUsersDto {
  users: UserDto[];
  count: number;
}

/** Members assigned to the acting user as guide (GET /users/enrollment/members). Same shape as {@link PaginatedUsersDto}. */
export type EnrollmentMembersListResponseDto = PaginatedUsersDto;

export function isUserForGeo(user) {
  return (
    'id' in user &&
    'firstName' in user &&
    'lastName' in user &&
    'memberStatus' in user &&
    'coords' in user &&
    'memberStatus' in user
  );
}

export interface UserReecapDto {
  callsThisYear: number;
  membersServedThisYear: number;
  needsAddressedThisYear: number;
  needsIdentifiedThisYear: number;
  pcpConnectionsThisYear: number;
  textsThisYear: number;
  visitsThisYear: number;
  totalMembersServed: number;
}

/** One row in `iam.group_members` where `member_id` is the user’s principal (not transitive). */
export interface UserIamGroupMembershipDto {
  groupPrincipalId: string;
  groupName: string;
}

/**
 * IAM snapshot for the acting user: direct group memberships and permission names implied by
 * IAM on the global **platform** sentinel only (see IAM docs). Policy binding rows are not exposed.
 */
export interface UserIamContextDto {
  principalId: string | null;
  groupMemberships: UserIamGroupMembershipDto[];
  /** Permission names (`CAN_*`) granted via IAM on the platform resource only. */
  platformPermissions: { [permissionName: string]: true };
}

export interface ActingUserDto extends UserDto {
  featureFlags: { [key: string]: boolean };
  permissions: { [key: string]: true };
  reecap?: UserReecapDto;
  /** Present after `/users/user/verify` when the user has (or can have) an IAM principal. */
  iam?: UserIamContextDto | null;
}

/** `users.feature_flag.key` for {@link ActingUserDto.featureFlags} — enrollment-style Expected Assigned Guide UI. */
export const VIEW_ENROLLMENT_FEATURE_FLAG = 'view-enrollment';

/** `users.feature_flag.key` for {@link ActingUserDto.featureFlags} — Google Places autocomplete in pre-call goals. */
export const PLACES_AUTOCOMPLETE_FEATURE_FLAG =
  'places-autocomplete' as const;

export interface AddressCreationRequestDto {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  primary?: boolean;
  description?: string;
}

export interface UpdateAddressRequestDto {
  primary: boolean;
  description: string;
}

export interface AddressCreationRequestDto {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  primary?: boolean;
  description?: string;
}

export interface UpdatePhoneNumberRequestDto {
  primary: boolean;
  description: string;
}

export interface PhoneNumberCreationRequestDto {
  number?: string;
  primary?: boolean;
  description?: string;
}

export interface UpdateUserSummaryRequestDto {
  text: string;
  isAutoUpdate: boolean;
}

export interface MemberStatusSelectOption {
  isSelectable: boolean;
  memberStatus: MemberStatusDto;
}
export interface MemberStatusDto {
  id: number;
  key: string;
  name: string;
  description: string;
  sortOrder?: number;

  effectiveAtQuestionText?: string;
  canRequestChangeTo?: boolean;
  changeRequestDescription?: string;
  changeRequestStageIds: number[];
  changeRequestStatusIds: number[];
}
export interface UserMemberStatusDto extends MemberStatusDto {
  memberId: string;
  memberStatusId: number;
  memberStatusChangeRequestId: number;
  interactionId: number;
  externalItemId: number;
  externalItemType: ExternalItemTypeDto;
  createdAt: number;
  effectiveAt: number;
}

export type StageKeyDto =
  | 'outreach'
  | 'support'
  | 'skill-building'
  | 'independence';

export interface MemberStageDto {
  id: number;
  key: StageKeyDto;
  name: string;
  description: string;
}

export interface MemberFiltersDto {
  events?: string[];
  statuses?: number[];
}

export interface ContactInfoType {
  address: 'address';
  phoneNumber: 'phoneNumber';
}

export interface TeamDto {
  id: number;
  name: string;
  userIds: string[];
}

export interface TeamHierarchyDto {
  id: number;
  name: string;
  teams: TeamDto[];
}

export interface VerifiedResponseDto {
  user: ActingUserDto;
  authenticated: boolean;
  voiceCallOutcomes: CallOutcomeDto[];
  visitOutcomes: VisitOutcomeDto[];
  memberStatuses: MemberStatusDto[];
  clients: ClientDto[];
}

export interface UpdateUserRequestDto {
  assignedGuideId?: string;
  cadence?: string;
  memberStageId?: number;
  namePronunciation?: string;
  preferredName?: string;
  pronouns?: string;
  reportedLanguage?: string;
  raceEthnicitySummary?: string;
}

export const UpdatePersonalInformationSchema = z.object({
  namePronunciation: z.string().optional(),
  preferredName: z.string().optional(),
  pronouns: z.string().optional(),
  userId: z.string().optional(),
  race: z.string().optional(),
  ethnicity: z.string().optional(),
  language: z.string().optional(),
});
